import { atomWithStorage } from 'jotai/utils'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'

/** What Home saw the last time it was open, so the next visit can say what changed since. */
export interface HomeVisit {
  /** When Home was last left (epoch ms). */
  at: number
  /** Total portfolio value at that moment, in USD; null when it hadn't loaded. */
  equityUsd: number | null
}

/** Per-user (the `dashboard-` key prefix namespaces it); null before the first visit. */
export const homeLastVisitAtom = atomWithStorage<HomeVisit | null>(
  STORAGE_KEYS.dashboard.homeLastVisit,
  null,
  safeStorage,
  { getOnInit: true }
)

/**
 * The daily brief the AI wrote (Home's "Today's brief"): a research job started on request,
 * polled until delivered, and kept so the next visit shows it without another run.
 */
export interface HomeAiBrief {
  jobId: string | null
  status: 'running' | 'done' | 'failed'
  requestedAt: number
  finishedAt?: number
  markdown?: string
  error?: string
  /** The ideas the brief was asked about, in prompt order ("## Idea 1" is ideaIds[0]). */
  ideaIds?: string[]
  /** The datapoint cards the brief was asked about, in prompt order ("D1" is datapointIds[0]). */
  datapointIds?: string[]
  /** The data sources the research run cited (tables/tools), for the brief's source chips. */
  sources?: string[]
}

export const homeAiBriefAtom = atomWithStorage<HomeAiBrief | null>(
  STORAGE_KEYS.dashboard.homeAiBrief,
  null,
  safeStorage,
  { getOnInit: true }
)

/**
 * Datapoint kinds the user asked to see less of ("Not useful" on a brief card); the brief picks
 * from the rest. Per user, kept across visits.
 */
export const homeHiddenDatapointsAtom = atomWithStorage<string[]>(
  STORAGE_KEYS.dashboard.homeHiddenDatapoints,
  [],
  safeStorage,
  { getOnInit: true }
)
